import { Download } from '@mui/icons-material';
import { Box, Button, Stack, Typography } from '@mui/material';
import { cloneDeep, concat, get, set } from 'lodash';
import React, { FC } from 'react';
import CreateTable from 'src/components/ui-component/form-component/create-form/CreateTable';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import { useApplication } from 'src/context/ApplicationContext';
import { RUS_PATH } from 'src/lib/constants/componentsPath';
import { ALS_ERLEDIGT_MARKIEREN_LABEL, V_PARTNER_KEINE_ANGABEN_TEXT } from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { ForceTrue, NonRequired } from 'src/lib/validation/ValidationTypes';
import PruefAssistenz from 'src/pages/private/online-antrag/antrag-components/PruefAssistenz';
import { InhaltTrenner, InhaltWrapper } from 'src/pages/private/online-antrag/antrag-components/Wrapper';
import { DownloadRusErklaerung } from 'src/pages/private/online-antrag/DownloadAntragPDF';
import { addIndexesToPath, checkReadOnlyInUeberarbeitung } from 'src/utils/helper';
import RusPostVersandInfo, { RUSNachdemDownloadInformation, RUSVordemUploadInformation } from './RusPostVersandInfo';

// import schemas
import { checkIfPruefAssitenzNeedsToValidate } from 'src/pages/private/online-antrag/application-helper-function/AppHelperfunctions';
import AntragSchema from '../../../../../../../lib/paths/antrag_FULL_Paths.json';
const P = AntragSchema.attributes;

const RusErklaerungenGlobal: FC<{ unternehmenId: number; unternehmenName: string; positionId?: string; hasComment?: boolean }> = (props) => {
	const { unternehmenId, positionId, unternehmenName, hasComment } = props;

	const { application, setApplicationProperty, saveApplication, readOnly } = useApplication();
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);

	const handleUpdateErklaerungDaten = (key: string, value: any, rusIndex: number) => {
		let appCopy = cloneDeep(application);
		set(appCopy, key, value);
		const unternehmenPath = addIndexesToPath(P.Selbsterklaerung_RUS.schema.attributes.Unternehmen.path, [`${rusIndex}`], ['Selbsterklaerung_RUS']);
		if (Number(get(appCopy, `${unternehmenPath}.id`, null)) > 0) {
			// DO Nothing, since the unternehmen ID already exist
		} else {
			set(appCopy, unternehmenPath, unternehmenId);
		}

		setApplicationProperty('attributes', get(appCopy, 'attributes'));
	};

	const handleGenerateSelbstErklaerung = async () => {
		DownloadRusErklaerung(application, unternehmenId, unternehmenName, positionId, readOnly, saveApplication);
	};

	if (unternehmenId) {
		return (
			<>
				{get(application, P.Selbsterklaerung_RUS.path, []).map((ele: any, i: number) => {
					if (
						(Number(get(ele, `${RUS_PATH.Unternehmen}.id`, null)) === unternehmenId && !get(ele, RUS_PATH.PositionID, null)) ||
						get(ele, RUS_PATH.PositionID, '') === positionId
					) {
						return (
							<React.Fragment key={i}>
								<PruefAssistenz
									statusPath={addIndexesToPath(
										P.Selbsterklaerung_RUS.schema.attributes.RUS_Pruefung_Pruefstatus.path,
										[`${i}`],
										['Selbsterklaerung_RUS'],
									)}
									hinweisPath={addIndexesToPath(
										P.Selbsterklaerung_RUS.schema.attributes.RUS_Pruefung_Hinnweis_Antragsteller.path,
										[`${i}`],
										['Selbsterklaerung_RUS'],
									)}
									ueberarbeitetAmPath={addIndexesToPath(
										P.Selbsterklaerung_RUS.schema.attributes.RUS_Pruefung_Ueberarbeitet_Am.path,
										[`${i}`],
										['Selbsterklaerung_RUS'],
									)}
									erledigtPath={addIndexesToPath(
										P.Selbsterklaerung_RUS.schema.attributes.RUS_Pruefung_Erledigt.path,
										[`${i}`],
										['Selbsterklaerung_RUS'],
									)}
									formStruc={mapFormStruc(formStrucPruefAssistenz(), i)}
								/>
								<Box sx={{ mt: '1rem' }}>
									<InhaltWrapper>
										<Typography variant={'h5'} component={'p'} sx={{ fontWeight: 600 }}>
											Download der RUS-Selbsterklärung
										</Typography>
										<Typography>Bitte laden Sie hier Ihre RUS-Selbsterklärung zur Unterzeichnung herunter:</Typography>
										<Stack direction={'row'} spacing={2}>
											<Button
												variant='outlined'
												startIcon={<Download />}
												sx={{ maxWidth: 'fit-content' }}
												onClick={() => handleGenerateSelbstErklaerung()}
											>
												RUS-Selbsterklärung für {unternehmenName}
											</Button>
										</Stack>
										<RUSNachdemDownloadInformation application={application} />
									</InhaltWrapper>
								</Box>

								<InhaltTrenner marginTop={'2rem'} />

								<InhaltWrapper>
									<RUSVordemUploadInformation application={application} />

									<CreateTable
										formStruc={mapFormStruc(formStruc(), i)}
										formValues={application}
										updateFormValuesFunc={(key, value) => handleUpdateErklaerungDaten(key, value, i)}
										readOnly={updatedReadOnly}
										columnsLabel={['Bezeichnungs des Dokuments', 'Datei']}
									/>
								</InhaltWrapper>

								<RusPostVersandInfo application={application} />
							</React.Fragment>
						);
					}
					return null;
				})}
			</>
		);
	}
	return <MUIAlert message={V_PARTNER_KEINE_ANGABEN_TEXT} />;
};

export default RusErklaerungenGlobal;

const mapFormStruc = (formStruc: FormStrucItem[], index: number) => {
	let result = formStruc.map((row) => {
		return {
			...row,
			fields: (get(row, 'fields', []) as Array<any>).map((value) => {
				return {
					...value,
					path: addIndexesToPath(get(value, 'path', ''), [`${index}`], ['Selbsterklaerung_RUS']),
				};
			}),
		};
	});
	return result;
};

const formStrucPruefAssistenz = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Selbsterklaerung_RUS.schema.attributes.RUS_Pruefung_Erledigt,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation: [{ validateFunction: ForceTrue }],
				},
			],
		},
	];

	return struc;
};
const formStruc = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{ path: 't.t', type: 'label', label: 'RUS-Selbsterklärung', validation: [{ validateFunction: NonRequired }] },
				{ ...P.Selbsterklaerung_RUS.schema.attributes.Unterschriebene_RUS_Selbsterklaerung, allowedFileNameKeywords: ['RUS-Selbsterklärung'] },
			],
		},
	];

	return struc;
};

export const getFormStrucRusErklaerungGlobal = (application: any, unternehmenId: number, positionId?: string) => {
	let formStrucValidation: FormStrucItem[] = [];

	get(application, P.Selbsterklaerung_RUS.path, []).forEach((ele: any, i: number) => {
		if (
			(get(ele, `${RUS_PATH.Unternehmen}.id`, -1) === unternehmenId && !get(ele, RUS_PATH.PositionID, null)) ||
			get(ele, RUS_PATH.PositionID, '') === positionId
		) {
			const isHinweisFieldRequired = checkIfPruefAssitenzNeedsToValidate(ele, RUS_PATH.Prufueng_Prufstatus, RUS_PATH.Prufueng_Erledigt);

			const formStrucPruefAssistenzMapped = mapFormStruc(formStrucPruefAssistenz(), i);
			const formStrucUploadErklaerung = mapFormStruc(formStruc(), i);

			if (isHinweisFieldRequired) {
				formStrucValidation = concat(formStrucPruefAssistenzMapped, formStrucUploadErklaerung);
			} else {
				formStrucValidation = concat(formStrucUploadErklaerung);
			}
		}
	});

	return formStrucValidation;
};
